// @givmo/compliance — module standing as a STRUCTURAL rule, not a convention (CCM-MOD1B).
//
// ══ THE MODULE IS GRANT-BY-EXCEPTION, AND THE PACKAGE ENFORCES IT AT ITS OWN BOUNDARY ══════════
//
// Compliance is a D-CPO-24 module: `default_allow=False` on the backend, held only through an
// explicit module grant or invitation. The host's console shell already answers "may this person
// open the module" for the tile, the sidebar row and the direct-link gate; what THIS file settles
// is the half those cannot see — the data plane. Every server operation the module owns fires only
// on an AFFIRMATIVE `'available'` standing from the host, and it refuses at the operation boundary
// otherwise. Two layers, on purpose, because either alone is a convention:
//
//   1. every page and panel folds `available` into its query `enabled` and renders no data region
//      without it (so no read is even scheduled);
//   2. `guardComplianceOperations` replaces every one of the module's server operations with a
//      refusal while standing is not `'available'`, so a page that forgot layer 1 — or a mutation a
//      stale button fires after a live revocation — still reaches no transport.
//
// The host's gate renders the standing ANSWER (locked, off, setting-up, unavailable-with-retry);
// this package renders none of those states and mints no copy for them.
//
// ══ THE CENSUS IS DERIVED, NEVER HAND-TRUSTED ══════════════════════════════════════════════════
//
// `COMPLIANCE_SERVER_OPERATIONS` names the module's server operations — every adapter method but the
// two non-module transports (`putArtifactBytes`, a signed storage PUT; and `resolveDisclosure`, the
// registry-served legal copy). It is typed two ways so another operation cannot be added
// silently: `satisfies` refuses a name the interface lacks, and
// `ComplianceServerOperationCensusIsComplete` refuses to compile while any interface method is
// missing from the list. The test suites re-derive the same set at runtime from a real adapter.

import type { ComplianceModuleStanding, ComplianceOperations } from './compliance-context';

/** The module's catalog slug — the key the console's standing read reports it under. */
export const COMPLIANCE_SLUG = 'compliance';

/**
 * The module's Member-catalog read string — the ONE permission every compliance page's service
 * requires, declared once here and named by every sidebar row the module declares in a host.
 *
 * Minted by the backend on the compliance catalog (CCM-MOD1B leg A, the CPA-BE28 `donations:read`
 * precedent): the org-baseline `charity:read` can never traverse a module standing check, which is
 * why this string REPLACES it on the module's thirteen read operations rather than sitting beside it.
 */
export const COMPLIANCE_READ_PERMISSION = 'compliance:read';

/**
 * The module's Owner string — held by a Compliance Owner by grant and by every Global Admin by tier,
 * never by a Member (D-CCM-18's Owner-level access; the backend's `APPROVE_COMPLIANCE_ACCESS_REQUESTS`
 * capability). The organization's "Use AI on your organization’s data" change requires it, and a
 * host's Settings panel for that setting is declared on it.
 */
export const COMPLIANCE_OWNER_PERMISSION = 'compliance:approve_access_requests';

/**
 * Sending a filing case's documents out for signature, and changing the organization's sign-in check
 * before signing (D-SIGN-2): an Owner-catalog string (a Compliance Owner by grant, a Global Admin by
 * tier), released only with signing. A host declares the sign-in check card on it, so where signing
 * is dark nobody holds it and the card is absent.
 */
export const COMPLIANCE_SEND_FOR_SIGNATURE_PERMISSION = 'compliance:send_for_signature';

/** The two adapter methods that are transports rather than module server operations. */
export type ComplianceNonModuleTransport = 'putArtifactBytes' | 'resolveDisclosure';

/** A module server operation — every adapter method that is not one of the two transports. */
export type ComplianceServerOperation = Exclude<
  keyof ComplianceOperations,
  ComplianceNonModuleTransport
>;

/**
 * The module's server operations, in the adapter's declaration order.
 *
 * ⚠ A NEW INTERFACE METHOD MUST BE ADDED HERE, and the compiler says so (see the type below). The
 * count is pinned by the tests' runtime derivation, never by a literal here.
 */
export const COMPLIANCE_SERVER_OPERATIONS = [
  'listObligations',
  'getObligation',
  // CCM-OPS1-REPIN: a filing's rule change, and its re-pin.
  'getObligationRuleChange',
  'repinObligation',
  // CCM-R1-TRACKING-PANEL: stopping and restarting a filing's tracking.
  'stopTracking',
  'resumeTracking',
  'listOccurrences',
  'recordSubmission',
  'recordWindowClosed',
  'recordRegistrationDate',
  'recordFiling',
  'issueArtifactUploadUrl',
  'confirmArtifact',
  'listArtifacts',
  'listObligationArtifacts',
  'getArtifactDownload',
  'deleteArtifact',
  'getProfile',
  'listProfileParameters',
  'recordProfileParameter',
  'listProfileSuggestions',
  'resolveProfileSuggestion',
  'listProfileReviewFlags',
  'resolveProfileReviewFlag',
  'listRegistrationImports',
  'importRegistration',
  'amendRegistrationImport',
  'listRegistrationImportAmendments',
  // CCM-MOD5B: the organization's registered agents and foreign qualifications.
  'listVendorRecords',
  'getVendorRecord',
  'recordVendor',
  // CCM-MOD5-RECORD-FILING-LINK: a record linked to the library filing it is, and undone.
  'linkVendorRecord',
  'unlinkVendorRecord',
  'getAiSetting',
  'setAiSetting',
  'listFilingOptions',
  'listFilingGroups',
  'createFilingGroup',
  'renameFilingGroup',
  'moveFilingGroup',
  'removeFilingGroup',
  'moveFilingToGroup',
  'listOrganizationFilings',
  'getOrganizationFiling',
  'addOrganizationFiling',
  'updateOrganizationFiling',
  'recordOrganizationFilingFiled',
  'recordOrganizationFilingSent',
  'addOrganizationFilingCycle',
  'listRenewalCycles',
  'recordRenewalFiling',
  'recordRenewalSubmission',
  'attachRenewalFiledCopy',
  'addRenewalAttachment',
  'attachObligationFiledCopy',
  'addObligationAttachment',
  'addEarlierRenewalCycle',
  'listFormAdapters',
  'listFilingCases',
  'openFilingCase',
  'listCasePreparations',
  'startFormPreparation',
  'getFormPreparation',
  'answerFormItem',
  'getFormPagePreview',
  'recordFormVersion',
  'getDocumentVersionDownload',
  'getFilingCaseChecklist',
  'attachChecklistDocument',
  'recordChecklistAct',
  'listCaseSigningRequests',
  'getSigningRequest',
  'getFilingCase',
  'getFilingDocument',
  'getDocumentVersionRendition',
  'signSigningRequest',
  'declineSigningRequest',
  'withdrawSignature',
  'getSigningSetting',
  'setSigningSetting',
  'getSigningPlaces',
  'issueSigningRequest',
  'reviseSigningRequest',
  'extendSigningRequest',
  'cancelSigningRequest',
  'getSigningRecordLines',
  'getSigningEvidence',
  'getSigningExport',
  'removeSigningIdentity',
  // CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS: whom that removal reaches, organization-wide.
  'listSigningPeople',
  'getRenewalCycle',
  'getSubmission',
  'authorizeSubmission',
  'revokeSubmissionAuthorization',
  'dispatchSubmission',
  'reconcileSubmissionAttempt',
  'getSubmissionPackage',
  // CCM-R1-SUBMIT-HANDOFF: what the organization sent itself.
  'recordSubmissionHandoff',
  // CCM-R1-RESULTS-WEB: what came back after a send.
  'recordSubmissionEvidence',
  'answerSubmissionEvidence',
  'listSubmissionFollowThrough',
  // CCM-R1-DESK-WEB: Givmo's filing desk.
  'handFilingToDesk',
  'takeFilingFromDesk',
  'resumeDesk',
  'getDeskStanding',
  'revokeDeskStanding',
  // CCM-R1-SIGN-OUTSIDE-B: a copy of a version signed outside Givmo.
  'recordExecutedCopy',
  // CCM-R1-REVIEW-WEB: the proposal and its review.
  'getProposal',
  'generateProposalRevision',
  'getProposalRevision',
  'listProposalItems',
  'listProposalResults',
  'recordProposalReview',
  'getProposalReview',
  // CCM-R1-REVIEW-ASSIGN: asking a teammate about a review item.
  'askAboutReviewItem',
  'withdrawReviewAsk',
  'listReviewAsks',
] as const satisfies readonly ComplianceServerOperation[];

/**
 * Compiles only while every {@link ComplianceServerOperation} is listed above. Adding a method to
 * `ComplianceOperations` without listing it here turns this alias into `never`-violating and fails
 * `typecheck` — the structural half of the "no silent new operation" rule.
 */
export type ComplianceServerOperationCensusIsComplete =
  Exclude<ComplianceServerOperation, (typeof COMPLIANCE_SERVER_OPERATIONS)[number]> extends never
    ? true
    : never;

// The compile-time proof itself: a `never` here is a type error, and an unlisted method makes it one.
export const COMPLIANCE_SERVER_OPERATION_CENSUS_COMPLETE: ComplianceServerOperationCensusIsComplete = true;

/**
 * The refusal an operation raises when the host has not affirmed the module's standing.
 *
 * Distinct from the transport's own errors on purpose: `isModuleUnavailable` is a 404 test (the
 * backend flag being dark), and a standing refusal is neither that nor a server answer — it is this
 * package declining to ask. Nothing renders it; the host's gate already said why.
 */
export class ComplianceStandingError extends Error {
  readonly standing: ComplianceModuleStanding;
  readonly operation: ComplianceServerOperation;

  constructor(operation: ComplianceServerOperation, standing: ComplianceModuleStanding) {
    super(`compliance: ${operation} refused — the module's standing is ${standing}, not available`);
    this.name = 'ComplianceStandingError';
    this.standing = standing;
    this.operation = operation;
  }
}

/** Whether an error is this package's own standing refusal. */
export function isComplianceStandingRefusal(error: unknown): error is ComplianceStandingError {
  return error instanceof ComplianceStandingError;
}

/**
 * The injected operations, exactly as given, when standing is `'available'`; otherwise a copy in
 * which every module server operation refuses without touching the underlying adapter.
 *
 * The two non-module transports pass through unchanged: a storage PUT can only follow an upload URL
 * a guarded operation issued, and the disclosure resolver is legal copy, not organization data.
 */
export function guardComplianceOperations(
  operations: ComplianceOperations,
  standing: ComplianceModuleStanding,
): ComplianceOperations {
  if (standing === 'available') return operations;
  const guarded: Record<string, unknown> = { ...operations };
  for (const operation of COMPLIANCE_SERVER_OPERATIONS) {
    guarded[operation] = () => Promise.reject(new ComplianceStandingError(operation, standing));
  }
  return guarded as unknown as ComplianceOperations;
}
